import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const AboutPage = () => {
  const heroRef = useScrollAnimation<HTMLDivElement>('fade-in-up');
  const missionRef = useScrollAnimation<HTMLDivElement>('fade-in-up');
  const valuesRef = useScrollAnimation<HTMLDivElement>('fade-in-up');
  const teamRef = useScrollAnimation<HTMLDivElement>('fade-in-up');
  const midwestRef = useScrollAnimation<HTMLDivElement>('fade-in-up');
  const ctaRef = useScrollAnimation<HTMLDivElement>('fade-in-up');

  return (
    <div className="min-h-screen bg-[#F5F5F3]">
      <Header />

      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center bg-[#1F2A44] mt-20">
        <div className="absolute inset-0 opacity-10">
          <div className="w-full h-full" style={{ backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,.05) 10px, rgba(255,255,255,.05) 20px)' }}></div>
        </div>
        <div ref={heroRef} className="relative max-w-[1440px] mx-auto px-20 w-full text-center">
          <div className="w-16 h-0.5 bg-[#B89C4B] mx-auto mb-6"></div>
          <h1
            className="text-[56px] text-white leading-tight mb-6"
            style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}
          >
            About Martell Investment Capital
          </h1>
          <p
            className="text-[18px] text-white/85 max-w-[640px] mx-auto"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            A Midwest-based multifamily investment firm built on disciplined underwriting, operational excellence, and community-minded ownership
          </p>
        </div>
      </section>

      {/* Mission Statement Section */}
      <section className="bg-white py-36">
        <div className="max-w-[1440px] mx-auto px-20">
          <div ref={missionRef} className="max-w-[840px] mx-auto text-center">
            <p
              className="text-[32px] text-[#1F2A44] leading-relaxed mb-14"
              style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}
            >
              "We acquire, improve, and operate multifamily properties with institutional discipline and community-minded purpose"
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-left">
              <p
                className="text-[16px] text-[#2E2E2E] leading-relaxed"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Martell Investment Capital was founded on the belief that disciplined real estate investing can create value for investors while improving housing quality and strengthening communities. We focus exclusively on multifamily properties in Midwest markets where we can apply our operational expertise and value-add strategy.
              </p>
              <p
                className="text-[16px] text-[#2E2E2E] leading-relaxed"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Our approach combines conservative financial analysis with hands-on asset management. We believe in transparency, alignment with our capital partners, and building long-term value through strategic improvements and operational excellence rather than financial engineering or market timing.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Grid Section */}
      <section className="bg-[#F5F5F3] py-32">
        <div className="max-w-[1440px] mx-auto px-20">
          <h2
            className="text-[42px] text-[#1F2A44] text-center mb-16"
            style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}
          >
            Our Values
          </h2>

          <div ref={valuesRef} className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: 'Disciplined Underwriting',
                description: 'Every acquisition is evaluated through conservative financial analysis, stress testing, and multiple exit scenarios to protect investor capital.',
              },
              {
                title: 'Operational Excellence',
                description: 'We implement structured property management systems that optimize operations, reduce expenses, and improve tenant satisfaction.',
              },
              {
                title: 'Transparency',
                description: 'Regular reporting, clear communication, and honest assessment of performance are fundamental to our investor relationships.',
              },
              {
                title: 'Community Focus',
                description: 'We believe in improving housing quality, safety, and neighborhood vitality through responsible property ownership.',
              },
              {
                title: 'Long-Term Perspective',
                description: 'Patient capital and sustainable value creation drive our strategy rather than short-term speculation or aggressive leverage.',
              },
              {
                title: 'Alignment',
                description: 'Our interests are aligned with our investors through co-investment, performance-based compensation, and shared long-term goals.',
              },
            ].map((value, index) => (
              <div
                key={index}
                className="bg-white p-10 rounded-lg border-l-4 border-[#B89C4B] shadow-md hover:shadow-xl transition-all duration-300"
                style={{
                  animationDelay: `${index * 0.1}s`,
                }}
              >
                <h3
                  className="text-[20px] text-[#1F2A44] font-semibold mb-4"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  {value.title}
                </h3>
                <p
                  className="text-[15px] text-[#2E2E2E] leading-relaxed"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Philosophy Section */}
      <section className="bg-white py-32">
        <div className="max-w-[1440px] mx-auto px-20">
          <div ref={teamRef} className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <img
                src="https://public.readdy.ai/ai/img_res/a62619ce67394bb3d84e19763a6e790e.jpg"
                alt="Our Approach"
                className="w-full h-[600px] object-cover rounded-lg shadow-lg"
              />
            </div>
            <div>
              <h2
                className="text-[38px] text-[#1F2A44] leading-tight mb-8"
                style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}
              >
                Our Approach to Partnership
              </h2>
              <div className="space-y-6">
                <p
                  className="text-[16px] text-[#2E2E2E] leading-relaxed"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  We view our investors as true partners in building long-term value. This means clear communication, regular reporting, and honest assessment of both opportunities and challenges.
                </p>
                <p
                  className="text-[16px] text-[#2E2E2E] leading-relaxed"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  Our team brings experience in real estate acquisition, property management, construction oversight, and financial analysis. We combine institutional discipline with entrepreneurial execution to identify and capture value-add opportunities.
                </p>
                <p
                  className="text-[16px] text-[#2E2E2E] leading-relaxed"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  We focus on what we know: multifamily properties in Midwest markets where we have local expertise, established relationships, and a proven track record of operational improvement.
                </p>
                <p
                  className="text-[16px] text-[#2E2E2E] leading-relaxed"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  Our commitment to responsible ownership extends beyond financial returns. We believe that improving housing quality, maintaining properties to high standards, and contributing positively to neighborhoods creates sustainable value for all stakeholders.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Midwest Section */}
      <section className="bg-[#1F2A44] py-32">
        <div className="max-w-[1440px] mx-auto px-20">
          <div ref={midwestRef} className="max-w-[900px] mx-auto text-center">
            <h2
              className="text-[42px] text-white leading-tight mb-8"
              style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}
            >
              Why the Midwest
            </h2>
            <p
              className="text-[17px] text-white/85 leading-relaxed mb-12"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              We focus on Midwest markets because they offer strong fundamentals, stable employment, affordable housing demand, and less competition from institutional capital. These markets provide attractive risk-adjusted returns with lower volatility than coastal gateway cities.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
              {[
                {
                  icon: 'ri-line-chart-line',
                  title: 'Stable Growth',
                  description: 'Consistent population and employment growth without the volatility of speculative markets',
                },
                {
                  icon: 'ri-money-dollar-circle-line',
                  title: 'Affordability',
                  description: 'Strong housing demand driven by affordability relative to coastal markets',
                },
                {
                  icon: 'ri-map-pin-line',
                  title: 'Local Expertise',
                  description: 'Deep market knowledge and established relationships in our target regions',
                },
              ].map((item, index) => (
                <div 
                  key={index} 
                  className="bg-white/8 border border-white/15 p-8 rounded-lg"
                  style={{
                    animationDelay: `${index * 0.15}s`,
                  }}
                >
                  <div className="w-14 h-14 mx-auto mb-5 border-2 border-[#B89C4B] rounded-full flex items-center justify-center">
                    <i className={`${item.icon} text-2xl text-[#B89C4B]`}></i>
                  </div>
                  <h3
                    className="text-[18px] text-white font-semibold mb-3"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    {item.title}
                  </h3>
                  <p
                    className="text-[15px] text-white/80 leading-relaxed"
                    style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 300 }}
                  >
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-white py-24">
        <div ref={ctaRef} className="max-w-[1440px] mx-auto px-20 text-center">
          <h2
            className="text-[38px] text-[#1F2A44] mb-5"
            style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}
          >
            Learn More About Our Approach
          </h2>
          <p
            className="text-[16px] text-[#2E2E2E] mb-8"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            Explore our investment strategy and current opportunities
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/strategy"
              className="px-10 py-3.5 bg-[#B89C4B] text-[#1F2A44] text-[15px] font-medium rounded transition-all duration-300 hover:bg-[#1F2A44] hover:text-white whitespace-nowrap cursor-pointer"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Our Strategy
            </Link>
            <Link
              to="/contact"
              className="px-10 py-3.5 border-2 border-[#1F2A44] text-[#1F2A44] text-[15px] font-medium rounded transition-all duration-300 hover:bg-[#1F2A44] hover:text-white whitespace-nowrap cursor-pointer"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;